import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Key, Mail, ShoppingBag, Palette, Send, Loader2, CheckCircle2, XCircle, PlayCircle } from "lucide-react";

import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";

export default function SystemSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [demoActive, setDemoActive] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
      
      try {
        const demoRes = await axios.get(`${API}/admin/demo/status`, {
          headers: getAuthHeaders()
        });
        setDemoActive(demoRes.data.is_active);
      } catch (err) {
        console.error("Failed to fetch demo status:", err);
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Fehler beim Laden der Einstellungen");
      // Set default settings on error
      setSettings({
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        smtp_encryption: "tls",
        smtp_enabled: false,
        sender_name: "EventHub",
        sender_email: "",
        reply_to_email: "",
        support_email: ""
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    console.log("Saving settings:", settings);
    try {
      const response = await axios.put(
        `${API}/admin/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      console.log("Save response:", response.data);
      toast.success("Einstellungen gespeichert");
      // Reload settings to verify
      await fetchSettings();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Fehler beim Speichern: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Bitte gib eine Test-E-Mail-Adresse ein");
      return;
    }

    // First save settings if smtp_host is set
    if (settings.smtp_host && !settings.smtp_host.includes("***")) {
      console.log("Auto-saving settings before test...");
      await handleSave();
    }

    setTestingEmail(true);
    setSmtpTestResult(null);

    console.log("Testing email with settings:", {
      smtp_host: settings.smtp_host,
      smtp_port: settings.smtp_port,
      smtp_username: settings.smtp_username,
      smtp_encryption: settings.smtp_encryption
    });

    try {
      const response = await axios.post(
        `${API}/admin/email/test`,
        { to_email: testEmailAddress },
        { headers: getAuthHeaders() }
      );
      console.log("Test response:", response.data);
      setSmtpTestResult({ success: true, message: response.data.message });
      toast.success("Test-E-Mail wurde gesendet!");
    } catch (error) {
      console.error("Test failed:", error.response?.data);
      const errorMsg = error.response?.data?.detail || "Verbindung fehlgeschlagen";
      setSmtpTestResult({ success: false, message: errorMsg });
      toast.error(`E-Mail-Test fehlgeschlagen: ${errorMsg}`);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleToggleDemo = async (checked) => {
    try {
      await axios.post(`${API}/admin/demo/toggle`, { is_active: checked }, { headers: getAuthHeaders() });
      setDemoActive(checked);
      toast.success(checked ? "Demo-Modus aktiviert! Perfektes Event generiert." : "Demo-Modus deaktiviert! Demo-Daten gelöscht.");
    } catch (error) {
      console.error("Failed to toggle demo mode:", error);
      toast.error("Fehler beim Umschalten des Demo-Modus");
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return <div className="text-center py-8">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl p-6 border border-border/50">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-medium text-foreground">System-Einstellungen</h3>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Speichert..." : "Speichern"}
          </Button>
        </div>

        <Tabs defaultValue="stripe" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="stripe">
              <Key className="w-4 h-4 mr-2" />Stripe
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="w-4 h-4 mr-2" />E-Mail
            </TabsTrigger>
            <TabsTrigger value="amazon">
              <ShoppingBag className="w-4 h-4 mr-2" />Amazon
            </TabsTrigger>
            <TabsTrigger value="branding">
              <Palette className="w-4 h-4 mr-2" />Branding
            </TabsTrigger>
            <TabsTrigger value="demo">
              <PlayCircle className="w-4 h-4 mr-2" />Demo Mode
            </TabsTrigger>
          </TabsList>

          {/* Stripe Settings */}
          <TabsContent value="stripe" className="space-y-4 mt-6">
            <div>
              <Label>Publishable Key</Label>
              <Input
                value={settings.stripe_publishable_key || ""}
                onChange={(e) => updateSetting("stripe_publishable_key", e.target.value)}
                placeholder="pk_test_..."
              />
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input
                type="password"
                value={settings.stripe_secret_key === "***SET***" ? "" : settings.stripe_secret_key || ""}
                onChange={(e) => updateSetting("stripe_secret_key", e.target.value)}
                placeholder={settings.stripe_secret_key === "***SET***" ? "Bereits gesetzt" : "sk_test_..."}
              />
              {settings.stripe_secret_key === "***SET***" && (
                <p className="text-xs text-green-600 mt-1">✓ Secret Key ist gesetzt</p>
              )}
            </div>
            <div>
              <Label>Webhook Secret</Label>
              <Input
                type="password"
                value={settings.stripe_webhook_secret === "***SET***" ? "" : settings.stripe_webhook_secret || ""}
                onChange={(e) => updateSetting("stripe_webhook_secret", e.target.value)}
                placeholder={settings.stripe_webhook_secret === "***SET***" ? "Bereits gesetzt" : "whsec_..."}
              />
              {settings.stripe_webhook_secret === "***SET***" && (
                <p className="text-xs text-green-600 mt-1">✓ Webhook Secret ist gesetzt</p>
              )}
            </div>
          </TabsContent>

          {/* Email Settings - SMTP Configuration */}
          <TabsContent value="email" className="space-y-6 mt-6">
            {/* SMTP Server Settings */}
            <div className="p-4 bg-secondary/30 rounded-xl space-y-4">
              <h4 className="font-medium text-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                SMTP Server Konfiguration
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Server</Label>
                  <Input
                    value={settings.smtp_host || ""}
                    onChange={(e) => updateSetting("smtp_host", e.target.value)}
                    placeholder="smtp.gmail.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">z.B. smtp.gmail.com, smtp.ionos.de</p>
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp_port || 587}
                    onChange={(e) => updateSetting("smtp_port", parseInt(e.target.value))}
                    placeholder="587"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Standard: 587 (TLS) oder 465 (SSL)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Benutzername / E-Mail</Label>
                  <Input
                    value={settings.smtp_username || ""}
                    onChange={(e) => updateSetting("smtp_username", e.target.value)}
                    placeholder="deine@email.de"
                  />
                </div>
                <div>
                  <Label>Passwort / App-Passwort</Label>
                  <Input
                    type="password"
                    value={settings.smtp_password === "***SET***" ? "" : settings.smtp_password || ""}
                    onChange={(e) => updateSetting("smtp_password", e.target.value)}
                    placeholder={settings.smtp_password === "***SET***" ? "••••••••" : "Passwort eingeben"}
                  />
                  {settings.smtp_password === "***SET***" && (
                    <p className="text-xs text-green-600 mt-1">✓ Passwort ist gesetzt</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Verschlüsselung</Label>
                  <Select
                    value={settings.smtp_encryption || "tls"}
                    onValueChange={(value) => updateSetting("smtp_encryption", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Verschlüsselung wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tls">TLS (Port 587)</SelectItem>
                      <SelectItem value="ssl">SSL (Port 465)</SelectItem>
                      <SelectItem value="none">Keine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={settings.smtp_enabled || false}
                    onCheckedChange={(checked) => updateSetting("smtp_enabled", checked)}
                  />
                  <Label>E-Mail-Versand aktivieren</Label>
                </div>
              </div>

              {/* Save reminder */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-700 font-medium">
                  ⚠️ Wichtig: Nach dem Ausfüllen bitte auf "Speichern" klicken (oben rechts), bevor Sie testen!
                </p>
              </div>
            </div>

            {/* Sender Details */}
            <div className="p-4 bg-secondary/30 rounded-xl space-y-4">
              <h4 className="font-medium text-foreground">Absender-Details</h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Absender Name</Label>
                  <Input
                    value={settings.sender_name || ""}
                    onChange={(e) => updateSetting("sender_name", e.target.value)}
                    placeholder="EventHub"
                  />
                </div>
                <div>
                  <Label>Absender E-Mail</Label>
                  <Input
                    type="email"
                    value={settings.sender_email || ""}
                    onChange={(e) => updateSetting("sender_email", e.target.value)}
                    placeholder="noreply@eventhub.de"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Reply-To E-Mail</Label>
                  <Input
                    type="email"
                    value={settings.reply_to_email || ""}
                    onChange={(e) => updateSetting("reply_to_email", e.target.value)}
                    placeholder="hello@eventhub.de"
                  />
                </div>
                <div>
                  <Label>Support E-Mail</Label>
                  <Input
                    type="email"
                    value={settings.support_email || ""}
                    onChange={(e) => updateSetting("support_email", e.target.value)}
                    placeholder="support@eventhub.de"
                  />
                </div>
              </div>
            </div>

            {/* Test Email */}
            <div className="p-4 bg-accent/10 rounded-xl space-y-4 border border-accent/30">
              <h4 className="font-medium text-foreground flex items-center gap-2">
                <Send className="w-4 h-4" />
                E-Mail-Verbindung testen
              </h4>
              <p className="text-sm text-muted-foreground">
                Sende eine Test-E-Mail um zu prüfen, ob die SMTP-Einstellungen korrekt sind.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="test@beispiel.de"
                  className="flex-1"
                />
                <Button
                  onClick={handleTestEmail}
                  disabled={testingEmail || !settings.smtp_host}
                  variant="outline"
                >
                  {testingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span className="ml-2 hidden sm:inline">Testen</span>
                </Button>
              </div>

              {smtpTestResult && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${smtpTestResult.success
                    ? "bg-green-500/10 text-green-600"
                    : "bg-red-500/10 text-red-600"
                  }`}>
                  {smtpTestResult.success ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{smtpTestResult.message}</span>
                </div>
              )}

              {!settings.smtp_host && (
                <p className="text-xs text-yellow-600">
                  ⚠️ Bitte erst SMTP-Server konfigurieren und speichern
                </p>
              )}
            </div>
          </TabsContent>

          {/* Amazon Affiliate Settings */}
          <TabsContent value="amazon" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <Label>Amazon Affiliate aktivieren</Label>
              <Switch
                checked={settings.amazon_affiliate_enabled || false}
                onCheckedChange={(checked) => updateSetting("amazon_affiliate_enabled", checked)}
              />
            </div>
            <div>
              <Label>Access Key</Label>
              <Input
                type="password"
                value={settings.amazon_access_key === "***SET***" ? "" : settings.amazon_access_key || ""}
                onChange={(e) => updateSetting("amazon_access_key", e.target.value)}
                placeholder={settings.amazon_access_key === "***SET***" ? "Bereits gesetzt" : "AKIA..."}
              />
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input
                type="password"
                value={settings.amazon_secret_key === "***SET***" ? "" : settings.amazon_secret_key || ""}
                onChange={(e) => updateSetting("amazon_secret_key", e.target.value)}
                placeholder={settings.amazon_secret_key === "***SET***" ? "Bereits gesetzt" : "Secret..."}
              />
            </div>
            <div>
              <Label>Associate Tag</Label>
              <Input
                value={settings.amazon_associate_tag || ""}
                onChange={(e) => updateSetting("amazon_associate_tag", e.target.value)}
                placeholder="eventhub-21"
              />
            </div>
            <div>
              <Label>Marktplatz</Label>
              <Input
                value={settings.amazon_marketplace || "de"}
                onChange={(e) => updateSetting("amazon_marketplace", e.target.value)}
                placeholder="de"
              />
              <p className="text-xs text-muted-foreground mt-1">z.B. de, com, co.uk, fr, it</p>
            </div>
            <div>
              <Label>Cache-Dauer (Stunden)</Label>
              <Input
                type="number"
                value={settings.amazon_cache_duration_hours || 24}
                onChange={(e) => updateSetting("amazon_cache_duration_hours", parseInt(e.target.value))}
              />
            </div>
          </TabsContent>

          {/* Branding Settings */}
          <TabsContent value="branding" className="space-y-4 mt-6">
            <div>
              <Label>App Name</Label>
              <Input
                value={settings.app_name || "EventHub"}
                onChange={(e) => updateSetting("app_name", e.target.value)}
                placeholder="EventHub"
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={settings.app_tagline || ""}
                onChange={(e) => updateSetting("app_tagline", e.target.value)}
                placeholder="Digitale Save-the-Date Karten"
              />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={settings.app_logo_url || ""}
                onChange={(e) => updateSetting("app_logo_url", e.target.value)}
                placeholder="https://..."
              />
            </div>
          </TabsContent>
          
          {/* Demo Mode Settings */}
          <TabsContent value="demo" className="space-y-4 mt-6">
            <div className="p-6 bg-accent/10 rounded-xl space-y-4 border border-accent/30 max-w-2xl">
              <h4 className="font-medium text-foreground flex items-center gap-2 text-xl">
                <PlayCircle className="w-6 h-6 text-primary" />
                Demo-Modus aktivieren
              </h4>
              <p className="text-muted-foreground leading-relaxed">
                Der Demo-Modus erstellt ein perfekt eingerichtetes Beispiel-Event, das alle Funktionen 
                und Module von Mokiplaner demonstriert. Mit Beispielgästen, Chatnachrichten, Aufgaben und mehr.
              </p>
              
              <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
                <div>
                  <h5 className="font-medium">Demo-Status</h5>
                  <p className="text-sm text-muted-foreground">Aktiviere / Deaktiviere das Demo-Event</p>
                </div>
                <Switch 
                  checked={demoActive}
                  onCheckedChange={handleToggleDemo}
                />
              </div>
              
              {demoActive && (
                <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-800">
                  <p className="font-medium flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Demo-Inhalte aktiv
                  </p>
                  <p className="text-sm mt-1">
                    Dein Demo-Event ist jetzt in deiner Event-Übersicht verfügbar!
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
